/*
 * Figma `116:2` — SELL-01 · Slip Review · Queue + Order Drawer.
 *
 * `TableTemplate`: the counts, the status tabs, the filter bar, the queue as a table, and the order
 * you opened in a drawer beside it. This is the screen an organizer lives in during a sale, and the
 * frame's change to it is a change of shape rather than of content — the shipped queue draws every
 * order as an expanded card, so twenty orders is a page nobody can scan and the decision "which one
 * needs me" is made by scrolling.
 *
 * A table answers that at a glance and moves the evidence into the drawer, where there is room for
 * the slip at a size somebody can actually read.
 *
 * This screen is deliberately **presentational**. Unlike the other Wave 2 destinations it takes no
 * hook of its own: the queue's data, polling, and every action already live in `Admin`, together
 * with role guards and provider rules that decide which buttons an order may even show
 * (`slipVerificationAllowsManualAction` and friends). Re-deriving those here would be a second
 * opinion about money, so the page passes them in and both shells run the same one.
 *
 * `renderSlip` and `renderDeliveries` are render props for the same reason: the slip viewer holds
 * an authenticated blob URL and the delivery history has its own fetch. Both stay where they are.
 */
import type { ReactNode } from 'react';
import {
  Alert, Button, Card, Drawer, FilterChips, KpiCard, Pagination, SearchField, StatusBadge,
  TableTemplate,
} from '..';
import type { BadgeTone, Column } from '..';
import type { Destination } from '../destinations';
import type { AdminOrderDto, OrderStatus } from '../../api';

export type ReviewTabKey = OrderStatus | 'problem' | 'checked_in' | '';

const STATUS_TONE: Record<string, BadgeTone> = {
  awaiting_verification: 'warning',
  paid: 'success',
  rejected: 'danger',
  expired: 'neutral',
  pending_payment: 'neutral',
  cancelled: 'neutral',
};

export interface SlipReviewScreenProps {
  destination: Destination;
  permitted: boolean;
  role?: string | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /**
   * TICK-160 — an outcome the operator must read that is *not* a load failure. `error` above replaces
   * the screen with "โหลดไม่สำเร็จ", which would tell someone whose approval succeeded that it failed.
   */
  notice?: string | null;
  noticeTitle?: string;
  noticeTone?: 'success' | 'warning';

  /** The page of orders currently shown, already filtered and sliced by the page. */
  orders: readonly AdminOrderDto[];
  /** Every order matching the current filter, for the result count. */
  totalOrders: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;

  tabs: readonly { key: ReviewTabKey; label: string; count: number | null }[];
  activeTab: ReviewTabKey;
  onTabChange: (key: ReviewTabKey) => void;

  query: string;
  onQueryChange: (next: string) => void;
  onExportCsv: () => void;
  exporting?: boolean;

  /** KPI values, already computed by the page from its stats. */
  kpis: readonly { label: string; value: string; meta?: string; tone?: 'positive' | 'warning' | 'danger' }[];

  openOrder: AdminOrderDto | null;
  onOpenOrder: (order: AdminOrderDto | null) => void;

  /** Whichever order is mid-action; its controls are disabled. */
  busyId: string | null;
  /** The actions an order may show — the page decides which apply, this draws what it is given. */
  actionsFor: (order: AdminOrderDto) => readonly {
    key: string;
    label: string;
    tone?: 'primary' | 'secondary' | 'ghost' | 'destructive';
    title?: string;
    run: () => void;
  }[];
  /** Why an order is waiting or was rejected, where the page can say. */
  reasonFor: (order: AdminOrderDto) => string | null;
  renderSlip: (order: AdminOrderDto) => ReactNode;
  renderDeliveries: (order: AdminOrderDto) => ReactNode;
  statusLabel: (status: OrderStatus) => string;
  formatMoney: (amount: number) => string;
  checkedInLabel: (order: AdminOrderDto) => string | null;
}

export function SlipReviewScreen(props: SlipReviewScreenProps) {
  const {
    destination, permitted, role, loading = false, error = null, onRetry, notice = null,
    noticeTitle = 'อนุมัติแล้ว แต่ยังส่งตั๋วไม่ได้', noticeTone = 'warning',
    orders, totalOrders, page, pageSize, onPageChange,
    tabs, activeTab, onTabChange, query, onQueryChange, onExportCsv, exporting = false,
    kpis, openOrder, onOpenOrder, busyId, actionsFor, reasonFor,
    renderSlip, renderDeliveries, statusLabel, formatMoney, checkedInLabel,
  } = props;

  const columns: Column<AdminOrderDto>[] = [
    {
      key: 'code',
      header: 'รหัส',
      width: '150px',
      cell: (o) => <span className="jt-mono">{o.code}</span>,
    },
    {
      key: 'buyer',
      header: 'ผู้ซื้อ',
      cell: (o) => (
        <span className="jt-cell-stack">
          <span>{o.name}</span>
          <span className="jt-cell-sub jt-mono">{o.phone}</span>
        </span>
      ),
    },
    {
      key: 'qty',
      header: 'จำนวน',
      align: 'end',
      width: '120px',
      cell: (o) => <span className="jt-mono">{o.qty} ใบ</span>,
    },
    {
      key: 'total',
      header: 'ยอด',
      align: 'end',
      width: '130px',
      cell: (o) => <span className="jt-mono">{formatMoney(o.total)}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '190px',
      cell: (o) => (
        <span className="jt-cell-stack">
          <StatusBadge tone={STATUS_TONE[o.status] ?? 'neutral'}>{statusLabel(o.status)}</StatusBadge>
          {/* The reason is what decides whether this row needs a person, so it rides in the
           * table rather than waiting inside the drawer. */}
          {reasonFor(o) && <span className="jt-cell-sub">{reasonFor(o)}</span>}
        </span>
      ),
    },
  ];

  const filtered = activeTab !== '' || query.trim().length > 0;

  return (
    <TableTemplate
      gate={{ destination, loading, error, onRetry, permitted, role }}
      kpis={kpis.map((kpi) => (
        <KpiCard key={kpi.label} label={kpi.label} value={kpi.value} meta={kpi.meta} tone={kpi.tone} />
      ))}
      toolbar={
        <>
          {notice && <Alert tone={noticeTone} title={noticeTitle}>{notice}</Alert>}
          <FilterChips
            chips={tabs.map((t) => ({ key: t.key, label: t.label, count: t.count ?? undefined }))}
            active={activeTab}
            onSelect={(key) => onTabChange(key as ReviewTabKey)}
            label="สถานะออร์เดอร์"
          />
          <SearchField
            value={query}
            onChange={onQueryChange}
            label="ค้นหาออร์เดอร์"
            placeholder="ค้นหา รหัส / เบอร์โทร / อีเมล / ชื่อ"
          />
          <Button size="sm" variant="secondary" disabled={exporting} onClick={onExportCsv}>
            {exporting ? 'กำลังเตรียมไฟล์…' : 'ดาวน์โหลด CSV'}
          </Button>
        </>
      }
      table={{
        label: 'คิวตรวจสอบสลิป',
        columns,
        rows: orders,
        rowKey: (o) => o.id,
        filtered,
        onClearFilters: () => { onTabChange(''); onQueryChange(''); },
        emptyTitle: 'ยังไม่มีออร์เดอร์',
        emptyText: 'เมื่อมีคนสั่งซื้อ ออร์เดอร์จะเข้ามาที่คิวนี้',
        onRowClick: (o) => onOpenOrder(o),
        activeRowKey: openOrder?.id ?? null,
        card: (o) => (
          <span className="jt-cell-stack">
            <span className="jt-mono">{o.code}</span>
            <span>{o.name} · {o.qty} ใบ · {formatMoney(o.total)}</span>
            <StatusBadge tone={STATUS_TONE[o.status] ?? 'neutral'}>{statusLabel(o.status)}</StatusBadge>
          </span>
        ),
        footer: (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalOrders}
            label="ออร์เดอร์"
            onPageChange={onPageChange}
          />
        ),
      }}
      drawer={
        <Drawer
          open={!!openOrder}
          onClose={() => onOpenOrder(null)}
          title={openOrder ? `ออร์เดอร์ ${openOrder.code}` : ''}
          footer={openOrder && actionsFor(openOrder).map((action) => (
            <Button
              key={action.key}
              variant={action.tone ?? 'secondary'}
              title={action.title}
              disabled={busyId === openOrder.id}
              onClick={action.run}
            >
              {action.label}
            </Button>
          ))}
        >
          {openOrder && (
            <>
              <div className="jt-inline-actions">
                <StatusBadge tone={STATUS_TONE[openOrder.status] ?? 'neutral'}>
                  {statusLabel(openOrder.status)}
                </StatusBadge>
                <span className="jt-field-hint">{openOrder.name} · {openOrder.phone}</span>
              </div>

              {reasonFor(openOrder) && (
                <Alert tone={openOrder.status === 'rejected' ? 'danger' : 'warning'} title="เหตุผล">
                  {reasonFor(openOrder)}
                </Alert>
              )}
              {checkedInLabel(openOrder) && (
                <Alert tone="success" title="รับ Wristband แล้ว">{checkedInLabel(openOrder)}</Alert>
              )}
              {/* TICK-160 — paid, and the ticket still cannot be sent; the approval is not in doubt. */}
              {openOrder.ticketDeliveryBlock && (
                <Alert tone="danger" title="ชำระแล้ว แต่ยังส่งตั๋วไม่ได้">{openOrder.ticketDeliveryBlock.message}</Alert>
              )}

              <Card title="รายการในออร์เดอร์">
                <ul className="jt-order-lines">
                  {openOrder.lines.map((line) => (
                    <li key={line.ticketId}>
                      <span>{line.label}</span>
                      <span className="jt-mono">{formatMoney(line.lineTotal)}</span>
                    </li>
                  ))}
                  {openOrder.couponCode && (
                    <li>
                      <span>โค้ด {openOrder.couponCode}</span>
                      <span className="jt-mono">−{formatMoney(openOrder.couponDiscount)}</span>
                    </li>
                  )}
                  <li className="jt-order-total">
                    <span>รวม {openOrder.qty} ใบ · เข้างานได้ {openOrder.entries} คน</span>
                    <span className="jt-mono">{formatMoney(openOrder.total)}</span>
                  </li>
                </ul>
              </Card>

              {openOrder.hasSlip && (
                <Card title="สลิปที่ลูกค้าส่งมา" sub="หลักฐานการโอน — ดูให้ชัดก่อนอนุมัติหรือปฏิเสธ">
                  {renderSlip(openOrder)}
                </Card>
              )}
              {/* TICK-146 — a ฿0 order is approved by a person on purpose; say why there is no slip. */}
              {!openOrder.hasSlip && openOrder.total === 0 && (
                <Card title="บัตรฟรี — ไม่มีสลิป">
                  ยอด ฿0 ผู้ซื้อไม่ต้องโอนเงิน — ตรวจชื่อและเบอร์ แล้วอนุมัติหรือปฏิเสธ
                </Card>
              )}

              <Card title="ประวัติการส่งบัตร">
                {renderDeliveries(openOrder)}
              </Card>
            </>
          )}
        </Drawer>
      }
    />
  );
}
